import { gql, useQuery } from '@apollo/client';

import type {
  GetSkillTaskNameQuery,
  GetSkillTaskNameQueryVariables,
  GetSkillTrainingRunsQuery,
  GetSkillTrainingRunsQueryVariables,
} from '@sbrc/gql/apollo-codegen';

// The `getTrainingRuns` resolver's `filter.tasks` is matched against task
// NAMES (not IDs) — same contract Marvin's own UI uses. We resolve the task
// name from the task ID first, then query runs by that name.
const GET_SKILL_TASK_NAME = gql`
  query GetSkillTaskName($id: String!) {
    getTrainingDataTaskById(id: $id) {
      id
      name
    }
  }
`;

// `downloads` is @include-d only when a caller opts in with a robotId, so
// pages that don't need download state (sidebar, General tab) skip the
// extra N+1 query on the backend.
const GET_SKILL_TRAINING_RUNS = gql`
  query GetSkillTrainingRuns(
    $taskName: String!
    $pagination: PaginationRequest
    $robotId: ID
    $includeDownloads: Boolean!
  ) {
    getTrainingRuns(filter: { tasks: [$taskName] }, pagination: $pagination) {
      items {
        id
        name
        created
        finished
        status
        epoch
        checkpointPath
        downloads(robotId: $robotId) @include(if: $includeDownloads) {
          id
          finished
          success
        }
      }
    }
  }
`;

// Mirrors the resolver's pagination contract — it dereferences
// `pagination.offset` unconditionally, so we must pass a concrete object
// rather than letting it be null. Same workaround as useSkillEpisodes.
const DEFAULT_PAGINATION = { offset: 0, limit: 200 };

export type SkillTrainingRun = NonNullable<
  GetSkillTrainingRunsQuery['getTrainingRuns']
>['items'][number];

export interface UseSkillTrainingRunsOptions {
  /**
   * When set, each run's `downloads` field is included and scoped to the
   * given robot. Callers that only need run metadata (sidebar, General tab)
   * can omit this.
   */
  robotId?: string | null;
  /**
   * Poll interval in ms for live status updates. Defaults to 0 (no polling).
   * The General tab flips this to 5000ms while a run is in flight.
   */
  pollInterval?: number;
}

export function useSkillTrainingRuns(
  taskId: string | null,
  options: UseSkillTrainingRunsOptions = {},
) {
  const { robotId = null, pollInterval = 0 } = options;
  const includeDownloads = robotId != null;

  const taskQuery = useQuery<
    GetSkillTaskNameQuery,
    GetSkillTaskNameQueryVariables
  >(GET_SKILL_TASK_NAME, {
    variables: { id: taskId ?? '' },
    skip: !taskId,
    fetchPolicy: 'cache-and-network',
  });

  const taskName = taskQuery.data?.getTrainingDataTaskById?.name ?? null;

  const runsQuery = useQuery<
    GetSkillTrainingRunsQuery,
    GetSkillTrainingRunsQueryVariables
  >(GET_SKILL_TRAINING_RUNS, {
    variables: {
      taskName: taskName ?? '',
      pagination: DEFAULT_PAGINATION,
      robotId,
      includeDownloads,
    },
    skip: !taskName,
    fetchPolicy: 'cache-and-network',
    pollInterval,
  });

  return {
    trainingRuns: runsQuery.data?.getTrainingRuns?.items ?? [],
    loading: taskQuery.loading || runsQuery.loading,
    error: taskQuery.error ?? runsQuery.error,
    // Refetch resolves the task name first, then passes the fresh name
    // explicitly to the runs query. Without this, React hasn't re-rendered
    // between the two awaits, so `runsQuery.refetch()` would fire with a
    // stale `taskName` from the previous render.
    refetch: async () => {
      const taskResult = await taskQuery.refetch();
      const freshTaskName = taskResult.data?.getTrainingDataTaskById?.name;

      if (freshTaskName) {
        await runsQuery.refetch({
          taskName: freshTaskName,
          pagination: DEFAULT_PAGINATION,
          robotId,
          includeDownloads,
        });
      }
    },
    startPolling: runsQuery.startPolling,
    stopPolling: runsQuery.stopPolling,
  };
}
